import { readonly, type Ref, ref } from "vue"
import {
  getAuth,
  onAuthStateChanged,
  signInAnonymously,
  signInWithCustomToken,
  signOut,
  type User,
} from "firebase/auth"
import { type Firestore, type Unsubscribe } from "firebase/firestore"
import { getCurrentUser, useFirestore } from "vuefire"
import { cleanupSubscriptions, setDbInstance } from "@/services/subscriptionManager"
import { useWowpress } from "./useWowpress"
import { useCrypto } from "./useCrypto"

// --- 모듈 스코프 상태 정의 (전역 상태) ---
const user: Ref<User | null> = ref(null) // Firebase User 객체
const loading = ref(true) // Auth 상태 로딩 (리스너 첫 응답까지)
const error: Ref<Error | null> = ref(null)
const isSigningIn = ref(false) // 로그인/가입 API 호출 중 상태
const chatInfoLoading = ref(false) // chatInfo 복호화/처리 중 상태

// --- 모듈 스코프 구독 관리 ---
let unsubscribeAuth: Unsubscribe | null = null
let isAuthListenerAttached = false // 리스너 등록 여부 플래그
let currentDbInstance: Firestore | null = null // Firestore 타입 사용

// --- 컴포저블 함수 ---
export function useAuth() {
  const auth = getAuth()
  const { getToken } = useCrypto()
  const localDb = useFirestore() // 컴포저블 내 지역 DB 인스턴스
  const { login: loginWowpress } = useWowpress()

  // --- Auth 상태 리스너 설정 (최초 1회 실행 보장) ---
  const setupAuthListener = () => {
    // 이미 리스너가 연결되어 있으면 아무것도 하지 않음
    if (isAuthListenerAttached) {
      console.log("[useAuth] Auth listener setup skipped: Already attached.")
      return
    }
    if (!auth) {
      console.error("[useAuth] Firebase Auth instance is not available.")
      loading.value = false
      error.value = new Error("Firebase Auth not initialized")
      return
    }

    console.log("[useAuth] Setting up Auth state listener.")
    loading.value = true // 리스너 설정 시작 시 로딩 상태 true
    unsubscribeAuth = onAuthStateChanged(
      auth,
      (firebaseUser) => {
        console.log("[useAuth] onAuthStateChanged fired. User UID:", firebaseUser?.uid ?? "null/undefined")
        // user 상태는 리스너 콜백에서 직접 업데이트 (Source of Truth)
        user.value = firebaseUser
        loading.value = false
        isAuthListenerAttached = true // 리스너가 성공적으로 연결됨
      },
      (authError) => {
        console.error("[useAuth] Auth listener error:", authError)
        error.value = authError
        user.value = null // 에러 발생 시 사용자 null 처리
        loading.value = false
        isAuthListenerAttached = false // 에러 발생 시 리스너 연결 실패로 간주
        if (unsubscribeAuth) {
          unsubscribeAuth() // 에러 발생 시 현재 리스너 해제 시도
          unsubscribeAuth = null
        }
      },
    )
  }

  // --- 사용자 인증 관련 함수들 ---
  const updateUserAndAuthenticate = async (encryptedData: string): Promise<boolean> => {
    try {
      chatInfoLoading.value = true
      console.log("[useAuth] 암호화된 사용자 데이터 처리 및 인증 시작...")
      const token = await getToken(encryptedData)

      if (!token) {
        console.error("[useAuth] 사용자 데이터 복호화 실패")
      }

      if (token) {
        console.log("[useAuth] 인증 정보 발견, 로그인 시도...")
        const loggedInUser = await signInWithCustomTokenHandler(token)
        return loggedInUser !== null
      } else {
        console.log("[useAuth] 인증 정보가 없습니다. 익명 로그인 진행...")
        await signInAnonymouslyHandler()
        // 익명 로그인은 성공 여부 반환보다는 상태 변경으로 처리
        return user.value?.isAnonymous ?? false // 익명 로그인 후 상태 기반 반환 (개선 가능)
      }
    } catch (e) {
      console.error("[useAuth] 사용자 데이터 처리 중 오류 발생:", e)
      error.value = e instanceof Error ? e : new Error("Error processing user data")
      try {
        await signInAnonymouslyHandler()
      } catch (signInError) {
        console.error("[useAuth] 익명 로그인 실패:", signInError)
      }
      return false
    } finally {
      chatInfoLoading.value = false
    }
  }

  const authenticateWithCredentials = async (username: string, password: string): Promise<User | null> => {
    let firebaseUser: User | null = null
    try {
      isSigningIn.value = true
      error.value = null
      const token = await loginWowpress(username, password)

      if (!token) {
        throw new Error("와우프레스 로그인 실패: 토큰이 없습니다.")
      }
      console.log("[useAuth] 와우프레스 로그인 성공, 토큰 획득")

      firebaseUser = await signInWithCustomTokenHandler(token)
      return firebaseUser
    } catch (e) {
      console.error("[useAuth] 와우프레스 로그인 실패:", e)
      error.value = e instanceof Error ? e : new Error("Failed to login with credentials")
      await signInAnonymouslyHandler() // 로그인 실패 시 익명 로그인 시도
      return null
    } finally {
      isSigningIn.value = false
    }
  }

  const signInAnonymouslyHandler = async () => {
    if (user.value) {
      console.debug("[useAuth] Already logged in as user:", user.value.uid)
      return // 이미 로그인된 상태면 아무 작업도 하지 않음
    }
    const authUser = await getCurrentUser()
    if (authUser) {
      console.debug("[useAuth] Already logged in as current user.")
      return // 이미 현재 사용자로 로그인된 상태면 아무 작업도 하지 않음
    }
    if (user.value && user.value.isAnonymous) {
      console.debug("[useAuth] Already logged in anonymously.")
      return // void 반환
    }
    isSigningIn.value = true
    error.value = null
    try {
      await signInAnonymously(auth)
      console.log("[useAuth] Anonymous sign in successful.")
      // onAuthStateChanged가 user.value 업데이트
    } catch (e) {
      console.error("[useAuth] Anonymous sign in error:", e)
      error.value = e as Error
    } finally {
      isSigningIn.value = false
    }
  }

  const signInWithCustomTokenHandler = async (token: string): Promise<User | null> => {
    if (!token) {
      console.error("[useAuth] Custom token required.")
      error.value = new Error("Custom token missing")
      return null
    }
    console.log("[useAuth] Attempting custom token sign in...")
    isSigningIn.value = true
    error.value = null
    try {
      const userCredential = await signInWithCustomToken(auth, token)
      const firebaseUser = userCredential.user
      console.log("[useAuth] Custom token sign in successful")

      return firebaseUser
    } catch (e) {
      console.error("[useAuth] Custom token sign in error:", e)
      error.value = e as Error
      return null
    } finally {
      isSigningIn.value = false
    }
  }

  const signOutUser = async () => {
    if (!user.value) return
    try {
      console.log("[useAuth] Attempting sign out...")
      await signOut(auth)
      console.log("[useAuth] Sign out successful.")
      // onAuthStateChanged가 user.value=null 처리 및 watch 트리거
    } catch (e) {
      console.error("[useAuth] Sign out error:", e)
      error.value = e as Error
    }
  }

  // --- 구독 해제 및 정리 ---
  const dispose = () => {
    console.log("[useAuth] Disposing auth instance...")
    if (unsubscribeAuth) {
      console.log("[useAuth] Unsubscribing from Auth state.")
      unsubscribeAuth()
      unsubscribeAuth = null
    }
    isAuthListenerAttached = false // 리스너 해제됨
    cleanupSubscriptions() // 티켓/메시지 구독 정리
    // 전역 상태 초기화는 앱 종료 시점에 따라 결정 (user 등)
    // loading.value = true; // 다시 로딩 상태로 만들 수 있음
  }

  // --- 초기화 ---
  // Firestore 인스턴스를 subscriptionManager에 설정 (최초 1회 보장 필요)
  if (localDb && !currentDbInstance) {
    console.log("[useAuth] Setting DB instance for subscriptionManager.")
    currentDbInstance = localDb // 내부에서 관리하거나
    setDbInstance(localDb) // subscriptionManager에 전달
  } else if (!currentDbInstance) {
    console.warn("[useAuth] Local DB instance not found during init.")
  }
  // Auth 상태 리스너 설정 (최초 1회)
  setupAuthListener()

  // --- 반환 값 ---
  return {
    user: readonly(user),
    loading: readonly(loading),
    isSigningIn: readonly(isSigningIn),
    error: readonly(error),
    chatInfoLoading: readonly(chatInfoLoading),
    // 인증 함수들
    signInAnonymously: signInAnonymouslyHandler,
    signInWithCustomToken: signInWithCustomTokenHandler,
    signOutUser,
    updateUserAndAuthenticate,
    authenticateWithCredentials,
    // 정리 함수
    dispose,
  }
}
